import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Tv, ArrowRight } from "lucide-react";

/**
 * Standalone, ultra-simple TV-first page reachable at `/tv` (or via the
 * short host `tv.bypassgo.app` once DNS is configured to alias this app).
 *
 * The TV user enters a 4-digit code with their remote ; we resolve it via
 * `GET /api/tv/resolve/{code}` and redirect to /display/{commerceId}.
 *
 * No header, no chrome, no auth — just 4 boxes, one CTA. Designed for very
 * basic TV browsers (no fancy layouts).
 */
export default function TVCodeEntry() {
  const nav = useNavigate();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    refs[0].current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (i, value) => {
    const v = (value || "").replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError(null);
    if (v && i < 3) refs[i + 1].current?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs[i - 1].current?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs[i - 1].current?.focus();
    } else if (e.key === "ArrowRight" && i < 3) {
      refs[i + 1].current?.focus();
    } else if (e.key === "Enter") {
      submit();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const next = ["", "", "", ""];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    setError(null);
    refs[Math.min(text.length, 3)].current?.focus();
  };

  const submit = async () => {
    const code = digits.join("");
    if (code.length !== 4) {
      setError("Saisissez les 4 chiffres du code TV.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.get(`/tv/resolve/${code}`);
      nav(`/display/${data.commerceId}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Code inconnu");
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1340] text-white flex flex-col items-center justify-center p-6" data-testid="tv-entry-page">
      <div className="flex items-center gap-3 mb-8">
        <Tv size={28} />
        <p className="font-display font-black tracking-tight text-2xl">
          <span style={{ color: "#FFFFFF" }}>Bypass</span><span style={{ color: "#2BB54A" }}>Go</span>
        </p>
      </div>

      <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-center max-w-2xl leading-tight" data-testid="tv-entry-title">
        Entrer le code écran public
      </h1>
      <p className="mt-3 text-sm sm:text-base text-white/70 text-center max-w-md">
        Saisissez avec votre télécommande le code à 4 chiffres affiché sur le tableau de bord de l'établissement.
      </p>

      <div
        className="mt-10 flex items-center gap-3 sm:gap-5"
        onPaste={onPaste}
      >
        {[0, 1, 2, 3].map((i) => (
          <input
            key={i}
            ref={refs[i]}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digits[i]}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
            className="w-16 h-20 sm:w-20 sm:h-24 md:w-24 md:h-28 text-center font-display font-black text-4xl sm:text-5xl md:text-6xl bg-white text-[#0B1340] border-4 border-white/20 rounded-md focus:outline-none focus:border-[#2BB54A]"
            data-testid={`tv-entry-digit-${i}`}
            aria-label={`Chiffre ${i + 1}`}
          />
        ))}
      </div>

      {error && (
        <p className="mt-5 text-[#F18F18] font-bold text-sm" data-testid="tv-entry-error">{error}</p>
      )}

      <button
        onClick={submit}
        disabled={submitting || digits.join("").length !== 4}
        className="mt-8 inline-flex items-center justify-center gap-2 h-14 px-10 bg-[#1B5BFF] hover:bg-[#0A4FE4] disabled:opacity-50 text-white font-display font-black tracking-tight text-lg rounded-md"
        data-testid="tv-entry-submit-btn"
      >
        {submitting ? "Chargement…" : (<>Ouvrir l'écran <ArrowRight size={18} /></>)}
      </button>

      <p className="mt-10 text-xs text-white/50 text-center max-w-md">
        Astuce : ce code est généré et stable par établissement. Il fonctionne sur tout navigateur internet TV (Smart TV, Android TV, Fire TV, etc.).
      </p>
    </div>
  );
}
